import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { GarageInternal } from "./components/object-draw/environment/garage_internal";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, List, ListItem, ListItemButton, ListItemText } from "@mui/material";
import { Scene, SceneViewLabel } from "./components/object-draw/scene";
import { useStoreDispatch, useStoreSelector } from "../../../../../hooks/StoreHooks";
import { FrmImpostazioniIniziali } from "./components/forms/frm_impostazioni_iniziali";
import { useNavigate, useParams } from "react-router-dom";
import { useDialogs } from "../../../../providers/dialogs_provider";
import { useDoorConfigurationBuilderActions } from "../../../../../store/reducers/door_configuration_builder";
import { useOverlapConfigurationsService } from "../../../../services/overlap_configurations_service";
import { getCurrentUser } from "../../../../core/common_functions";
import { DoorOption, DoorOptionType, FormDimensioniModel, Option } from "../../../../models/door_configuration";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { ALTEZZA_GARAGE, LARGHEZZA_GARAGE, PROFONDITA_INVOLUCRO, PROFONDITA_INVOLUCRO_INTERNO } from "../../../../core/common_constants";
import { FormDimensioniOverModel } from "../../../../models/overbas_configuration";

export const DimensionsPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { base, doorConfigurationBuilder } = useStoreSelector(store => store);
  const dialogs = useDialogs();
  const dispatcher = useStoreDispatch();
  const { setStepActive, setStepComplete, setStepVisible, setLarghezzaPorta, setAltezzaPorta } = useDoorConfigurationBuilderActions();
  const configurationService = useOverlapConfigurationsService();
  const currentUser = getCurrentUser(base);
  const [isSubmit, setSubmit] = useState<boolean>(false);
  const [dialogOptions, setDialogOptions] = useState<DoorOption[] | null>(null);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const closeDialogOptions = () => {
    setDialogOptions(null);
  };

  const submit = async () => {
    try {
      if (!doorConfigurationBuilder.configuration) throw "invalid configuration";

      if (!doorConfigurationBuilder.configuration) throw { severity: "error", message: "Nessuna configurazione in corso" };
      if (doorConfigurationBuilder.configuration.larghezzaForo <= 0) throw { severity: "warning", message: "Larghezza non valida" };
      if (doorConfigurationBuilder.configuration.altezzaForo <= 0) throw { severity: "warning", message: "Altezza non valida" };
      if (doorConfigurationBuilder.configuration.profonditaGarage < doorConfigurationBuilder.configuration.altezzaForo) throw { severity: "warning", message: "Profondità garage non valida" };

      setSubmit(true);

      await configurationService.verificaVincoliDimensioni(doorConfigurationBuilder.configuration);

      let result = await configurationService.cercaOpzioniPorta(currentUser, doorConfigurationBuilder.configuration);

      let widthOptions = result.filter(o => o.proprieta == "width");
      let heightOptions = result.filter(o => o.proprieta == "height");

      if (widthOptions.length == 1) widthOptions[0].isSelected = true;
      if (heightOptions.length == 1) heightOptions[0].isSelected = true;

      if (!widthOptions.find(o => o.valore >= 2000)) throw { severity: "warning", message: "Larghezza non consentita. Consentita solo installazione Oltre Luce." };
      if (!heightOptions.find(o => o.valore >= 1980)) throw { severity: "warning", message: "Altezza non consentita. Consentita solo installazione Oltre Luce." };

      setDialogOptions(result);

      setSubmit(false);      
    } catch (e: any) {
      setSubmit(false);

      if (typeof e == "string") {
        dialogs?.showError("Attenzione...", e);
      } else {
        if (e.severity == "error") {
          dialogs?.showError("Attenzione...", e.message);
        } else if (e.severity == "warning") {
          dialogs?.showWarning("Attenzione...", e.message);
        }
      }
    }
  }

  const toggleOption = (selected: DoorOption) => {
    setDialogOptions(prev => prev?.map(o => ({
      ...o,
      isSelected: o.proprieta === selected.proprieta ? o.tipo === selected.tipo : o.isSelected
    })) ?? null);
  };

  const getOptionLabel = (tipo: DoorOptionType): string => {
    switch (tipo) {
      case "larghezza_standard":
        return "Misura standard";
      case "altezza_standard":
        return "Misura standard inferiore";
      case "altezza_superiore":
        return "Misura standard superiore";
      case "non_standard":
        return "Misura non standard (con sovrapprezzo)";
      default:
        return "Misura standard";
    }
  };

  const selectOption = async () => {
    const selectedWidth = dialogOptions?.find(o => o.proprieta == "width" && o.isSelected);
    const selectedHeight = dialogOptions?.find(o => o.proprieta == "height" && o.isSelected);

    if (!selectedWidth || !selectedHeight) {
      dialogs?.showWarning("Attenzione...", "Selezionare un'opzione per larghezza e altezza");
      return;
    }

    dispatcher(setLarghezzaPorta(selectedWidth.valore));
    dispatcher(setAltezzaPorta(selectedHeight.valore));

    closeDialogOptions();

    setSubmit(true);
    try {
      if (!doorConfigurationBuilder.configuration) throw "invalid configuration";

      let data: FormDimensioniModel = {
        quantita: doorConfigurationBuilder.configuration.quantita,
        vanoConAltroAccesso: doorConfigurationBuilder.configuration.vanoConAltroAccesso,
        larghezzaForo: doorConfigurationBuilder.configuration.larghezzaForo,
        altezzaForo: doorConfigurationBuilder.configuration.altezzaForo,
        larghezzaPorta: selectedWidth.valore,
        altezzaPorta: selectedHeight.valore,
        tipoLarghezza: selectedWidth.tipo,
        tipoAltezza: selectedHeight.tipo,
        profonditaGarage: doorConfigurationBuilder.configuration.profonditaGarage,
        altezzaArchitrave: doorConfigurationBuilder.configuration.altezzaArchitrave,
        spessoreArchitrave: doorConfigurationBuilder.configuration.spessoreArchitrave,
        larghezzaSpallettaSx: doorConfigurationBuilder.configuration.larghezzaSpallettaSx,
        spessoreSpallettaSx: doorConfigurationBuilder.configuration.spessoreSpallettaSx,
        larghezzaSpallettaDx: doorConfigurationBuilder.configuration.larghezzaSpallettaDx,
        spessoreSpallettaDx: doorConfigurationBuilder.configuration.spessoreSpallettaDx,
        isMotorizzata: doorConfigurationBuilder.configuration.isMotorizzata,
        tipoInstallazioneMontanteDx: doorConfigurationBuilder.configuration.tipoInstallazioneMontanteDx,
        tipoInstallazioneMontanteSx: doorConfigurationBuilder.configuration.tipoInstallazioneMontanteSx,
        tipoInstallazioneTraversa: doorConfigurationBuilder.configuration.tipoInstallazioneTraversa,
        tipoMolle: doorConfigurationBuilder.configuration.tipoMolle,
      };
      await configurationService.updateDimensions(currentUser, doorConfigurationBuilder.configuration.configurationId, data);
      dispatcher(setStepComplete({ stepSlug: "dimensioni", isComplete: true }));

      navigate(`/overlap-configurations/${id}/aspetto`);
    } catch (e: any) {
      setSubmit(false);

      if (typeof e == "string") {
        dialogs?.showError("Attenzione...", e);
      } else {
        if (e.severity == "error") {
          dialogs?.showError("Attenzione...", e.message);
        } else if (e.severity == "warning") {
          dialogs?.showWarning("Attenzione...", e.message);
        }
      }
    }
  }

  useEffect(() => {
    if (!id || isNaN(Number(id)) || !doorConfigurationBuilder.configuration) {
      navigate("/configurations");
    } else {
      dispatcher(setStepActive({ stepSlug: "dimensioni", isActive: true }));      
    }
  }, []);

  return (
    <>
      <div className="row m-0 h-full">
        <div className="col-xl-7 p-0 disegn-render d-none d-xl-block" style={{ position: "relative", height: "100%" }} >
            <Scene 
              bbox={{
                x: -PROFONDITA_INVOLUCRO,
                y: -PROFONDITA_INVOLUCRO,
                width: LARGHEZZA_GARAGE+(2*PROFONDITA_INVOLUCRO),
                height: ALTEZZA_GARAGE+(2*PROFONDITA_INVOLUCRO)
              }}
              sidebarSx={(<SceneViewLabel text="VISTA INTERNA" rotation="-90deg" />)}
              panEnabled 
              zoomEnabled>
                <GarageInternal>
                  
                </GarageInternal>
            </Scene>
        </div>
        <div className="col-xl-5 p-0 form-input-render">
          <div className="p-4">
            <h5 className="text-primary">Nuova configurazione</h5>
            <FrmImpostazioniIniziali />

            <div className="d-flex flex-row-reverse mt-3">
              <Button disabled={isSubmit} variant="contained" className="ps-3 pe-1" onClick={() => submit()}>
                <span>Avanti</span>
                <NavigateNextIcon />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Dialog
        fullScreen={fullScreen}
        maxWidth={"lg"}
        open={dialogOptions != null}
        onClose={closeDialogOptions}>
        <DialogTitle textAlign={"center"}>Misure passaggio</DialogTitle>
        <DialogContent sx={{ backgroundColor: "#f1f1f1" }} className="pt-3 pb-1">
          <div>
            <h5 className="m-0 mb-2">Larghezza passaggio</h5>
            {(() => {
              const opts = dialogOptions?.filter(o => o.proprieta == "width") ?? [];
              if (opts.length === 1) {
                return <div className="d-flex justify-content-between"><span>Misura porta:</span><strong>{opts[0].valore} mm</strong></div>;
              }
              return (
                <List disablePadding>
                  {opts.map((o, idx) => (
                    <ListItem key={`width_${idx}`} disablePadding>
                      <ListItemButton selected={o.isSelected} onClick={() => toggleOption(o)}>
                        <ListItemText>
                          <div className="d-flex justify-content-between">
                            <span className="me-3">{getOptionLabel(o.tipo)}</span>
                            <strong>{o.valore} mm</strong>
                          </div>
                        </ListItemText>
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              );
            })()}
          </div>

          <div className="mt-3">
            <h5 className="m-0 mb-2">Altezza passaggio</h5>
            {(() => {
              const opts = dialogOptions?.filter(o => o.proprieta == "height") ?? [];
              if (opts.length === 1) {
                return <div className="d-flex justify-content-between"><span>Misura porta:</span><strong>{opts[0].valore} mm</strong></div>;
              }
              return (
                <List disablePadding>
                  {opts.map((o, idx) => (
                    <ListItem key={`height_${idx}`} disablePadding>
                      <ListItemButton selected={o.isSelected} onClick={() => toggleOption(o)}>
                        <ListItemText>
                          <div className="d-flex justify-content-between">
                            <span className="me-3">{getOptionLabel(o.tipo)}</span>
                            <strong>{o.valore} mm</strong>
                          </div>
                        </ListItemText>
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              );
            })()}
          </div>
        </DialogContent>
        <DialogActions>
          <Button color="secondary" onClick={closeDialogOptions}>
            Annulla
          </Button>
          <Button variant="contained" color="primary" onClick={selectOption} disabled={
            !dialogOptions?.find(o => o.proprieta == "width" && o.isSelected) ||
            !dialogOptions?.find(o => o.proprieta == "height" && o.isSelected)
          }>
            Continua
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}